/**
 * Utilitários para filtros de data na plataforma
 * Inclui filtro "Hoje" com fuso horário do Brasil
 */

export interface DateFilterRange {
  start: string;
  end: string;
  label: string;
}

export type PeriodType = 'general' | 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'custom';

/**
 * Obtém as datas de hoje no fuso horário do Brasil
 * @returns Objeto com data de início (00:00) e fim (agora)
 */
export const getTodayDates = (): DateFilterRange => {
  const now = new Date();
  const brazilTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Sao_Paulo"}));
  
  // Data de hoje às 00:00:00 (início do dia)
  const todayStart = new Date(brazilTime);
  todayStart.setHours(0, 0, 0, 0);
  
  // Data atual (fim do dia até agora)
  const todayEnd = new Date(brazilTime);
  
  return {
    start: todayStart.toISOString(),
    end: todayEnd.toISOString(),
    label: 'Hoje'
  };
};

/**
 * Obtém as datas de ontem no fuso horário do Brasil
 * @returns Objeto com data de início (00:00) e fim (23:59:59)
 */
export const getYesterdayDates = (): DateFilterRange => {
  const now = new Date();
  const brazilTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Sao_Paulo"}));
  
  // Data de ontem às 00:00:00 (início do dia)
  const yesterdayStart = new Date(brazilTime);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  yesterdayStart.setHours(0, 0, 0, 0);
  
  // Data de ontem às 23:59:59 (fim do dia)
  const yesterdayEnd = new Date(brazilTime);
  yesterdayEnd.setDate(yesterdayEnd.getDate() - 1);
  yesterdayEnd.setHours(23, 59, 59, 999);
  
  return {
    start: yesterdayStart.toISOString(),
    end: yesterdayEnd.toISOString(),
    label: 'Ontem'
  };
};

/**
 * Obtém as datas para os últimos N dias
 * @param days Número de dias para trás
 * @returns Objeto com data de início e fim
 */
export const getLastDaysDates = (days: number): DateFilterRange => {
  const now = new Date();
  const brazilTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Sao_Paulo"}));
  
  // Data de início (N dias atrás às 00:00)
  const startDate = new Date(brazilTime);
  startDate.setDate(startDate.getDate() - days);
  startDate.setHours(0, 0, 0, 0);
  
  // Data atual
  const endDate = new Date(brazilTime);
  
  return {
    start: startDate.toISOString(),
    end: endDate.toISOString(),
    label: `Últimos ${days} dias`
  };
};

/**
 * Converte um período em parâmetros de data para API
 * @param period Tipo de período
 * @param customStart Data de início personalizada (para período custom)
 * @param customEnd Data de fim personalizada (para período custom)
 * @returns Parâmetros para enviar à API
 */
export const getPeriodParams = (
  period: PeriodType,
  customStart?: string,
  customEnd?: string
): Record<string, string> => {
  const params: Record<string, string> = {};
  
  switch (period) {
    case 'general':
      // Não adiciona parâmetros de data para mostrar dados gerais
      break;
      
    case 'today':
      const todayDates = getTodayDates();
      params.dateFrom = todayDates.start;
      params.dateTo = todayDates.end;
      break;
      
    case 'yesterday':
      const yesterdayDates = getYesterdayDates();
      params.dateFrom = yesterdayDates.start;
      params.dateTo = yesterdayDates.end;
      break;
      
    case '7d':
      const weekDates = getLastDaysDates(7);
      params.dateFrom = weekDates.start;
      params.dateTo = weekDates.end;
      break;
      
    case '30d':
      const monthDates = getLastDaysDates(30);
      params.dateFrom = monthDates.start;
      params.dateTo = monthDates.end;
      break;
      
    case '90d':
      const quarterDates = getLastDaysDates(90);
      params.dateFrom = quarterDates.start;
      params.dateTo = quarterDates.end;
      break;
      
    case 'custom':
      if (customStart) params.dateFrom = customStart;
      if (customEnd) params.dateTo = customEnd;
      break;
  }
  
  return params;
};

/**
 * Formata uma data para exibição no fuso horário do Brasil
 * @param dateString String da data (ISO)
 * @param options Opções de formatação
 * @returns Data formatada
 */
export const formatBrazilDate = (
  dateString: string,
  options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }
): string => {
  const date = new Date(dateString);
  return date.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    ...options
  });
};

/**
 * Verifica se uma data está dentro do período "hoje"
 * @param dateString String da data para verificar
 * @returns true se a data é de hoje
 */
export const isToday = (dateString: string): boolean => {
  const todayDates = getTodayDates();
  const date = new Date(dateString);
  const start = new Date(todayDates.start);
  const end = new Date(todayDates.end);
  
  return date >= start && date <= end;
};

/**
 * Obtém o horário atual no fuso horário do Brasil
 * @returns String formatada do horário atual
 */
export const getCurrentBrazilTime = (): string => {
  return new Date().toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

/**
 * Obtém a data atual no fuso horário do Brasil
 * @returns String formatada da data atual
 */
export const getCurrentBrazilDate = (): string => {
  return new Date().toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo'
  });
}; 